import { useMemo } from "react";
import { useAuth } from "@/auth/auth-provider";
import { normalizePermissions, PERMISSIONS, type Permission } from "@/lib/permissions";
import { useRecord, useRecords } from "@/sync/hooks";

export type Permissions = {
	can: (permission: Permission) => boolean;
	isOwner: boolean;
	roleId: string | undefined;
	roleName: string | undefined;
	memberId: string | undefined;
};

/**
 * The current user's permissions in the active workspace. Reads the synced role + membership so
 * edits made by an admin apply live; falls back to the login payload until sync has them.
 * UI gating only — the API enforces every permission.
 */
export function usePermissions(): Permissions {
	const { user, workspace } = useAuth();
	const [member] = useRecords("workspaceMember", { where: { userId: user?.id ?? "" } });
	const role = useRecord("workspaceRole", member?.roleId ?? workspace?.roleId);

	return useMemo(() => {
		const isOwner = role ? role.systemKey === "owner" : !!workspace?.isOwner;
		const held = new Set<Permission>(
			isOwner
				? PERMISSIONS
				: role
					? normalizePermissions(role.permissions)
					: (workspace?.permissions ?? []),
		);
		return {
			can: (permission) => held.has(permission),
			isOwner,
			roleId: role?.id ?? workspace?.roleId,
			roleName: role?.name ?? workspace?.roleName,
			memberId: member?.id,
		};
	}, [role, member, workspace]);
}

export const useCan = (permission: Permission) => usePermissions().can(permission);
